import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { getClassificationKeyValuesForCountry } from 'common/igaSubClassification/igaSubClassificationHelper';

import { isRequired } from 'components/Questionnaires/validators';
import { sortArrayByLabel } from 'common/language/intlArrayHelper';

export const QuestionIgaSubClassificationDropdownField = ({ intl, blockId, question, className, onChange, jurisdiction }) => {
  const [inputValue, setInputValue] = useState(question?.answer ?? question?.default ?? '');
  const igaSubClassificationCombos = useSelector(state => state.app?.staticData?.igaSubClassificationsCombos ?? []);
  const jurisdictions = useSelector(state => state.app?.staticData?.jurisdictions ?? []);
  const locale = useSelector(state => state.app?.locale ?? 'en');
  const defaultLocale = 'en';

  const onInputChange = (event) => {
    setInputValue(event.target.value);
    onChange(question, event.target.value);
    return true;
  };

  const questionProps = () => {
    const props = Object.assign({}, question.componentProps);
    props.items = undefined; // Clear items so it doesn't display in html
    // eslint-disable-next-line react/prop-types
    delete props.preventTranslation; // Clear preventTranslation so it doesn't display in html
    // eslint-disable-next-line react/prop-types
    delete props.treatiesOnly; // Clear treatiesOnly so it doesn't display in html
    return props;
  };

  useEffect(() => {
    if (inputValue) {
      onChange(question, inputValue);
    }
  }, []);

  useEffect(() => {
    if (inputValue !== question.answer) {
      setInputValue(question.answer ?? '');
    }
  }, [question.answer]);

  const getSubclassificationsForJurisdiction = () => {
    const jurisdictionsDictionary = new Map(jurisdictions.map(j => [j.countryCodeAlpha, j.isoJurisdictionName]));
    const jurisdictionName = jurisdictionsDictionary.get(jurisdiction);
    const subclassificationsForCountry = getClassificationKeyValuesForCountry(jurisdictionName, igaSubClassificationCombos);

    if (subclassificationsForCountry && locale === defaultLocale) {
      return Object.entries(subclassificationsForCountry)?.map(([key, value]) => ({ key, value }));
    }

    const translatedIgaSubclassifications = [];
    let localisedSubclassifications = intl.messages['questionnaire.igaSubclassifications'];
    localisedSubclassifications = Array.isArray(localisedSubclassifications) ? localisedSubclassifications : [];

    const translationsDictionary = new Map(localisedSubclassifications.map(s => [s.value, s.label]));

    if (subclassificationsForCountry) {
      Object.keys(subclassificationsForCountry).forEach((subclassification) => {
        const keyValuePair = { key: subclassification, value: translationsDictionary[subclassification] };
        translatedIgaSubclassifications.push(keyValuePair);
      });
    }

    return translatedIgaSubclassifications;
  };

  const updateDropDownItems = () => {
    const newItems = [];
    if (igaSubClassificationCombos && Object.keys(igaSubClassificationCombos).length) {
      newItems.push({ value: '', label: intl.formatMessage({ id: 'questionnaire.fields.QuestionIgaSubClassificationDropdown.default' }) });

      const translatedIgaSubclassifications = getSubclassificationsForJurisdiction();
      if (translatedIgaSubclassifications) {
        translatedIgaSubclassifications.map(subclassification => (
          newItems.push({ value: subclassification.key, label: subclassification.value })
        ));
      }
      return sortArrayByLabel(newItems, '', 'Other');
    }
    return [];
  };

  const [dropdownItems, setDropDownItems] = useState(updateDropDownItems());

  useEffect(() => {
    const newItems = updateDropDownItems();
    setDropDownItems(newItems);
  }, [igaSubClassificationCombos, locale, intl, jurisdiction]);

  return (<select
    key={ `${ blockId }-${ question.id }` }
    id={ `${ blockId }-${ question.id }` }
    name={ question.id }
    value={ inputValue }
    onChange={ e => onInputChange(e) }
    autoComplete="no"
    className={ `${ className }${ question.isValid === false ? ' error' : '' }` }
    required={ isRequired(question) }
    { ...questionProps() }
    disabled={ question.disabled }
    aria-label={ (question.placeholder ?? question.label) ? intl.formatMessage({ id: question.placeholder ?? question.label }) : '' }
    aria-required={ isRequired(question) }
    aria-describedby={ question.isValid === false ? (`${ blockId }-${ question.id }-error`) : undefined }
  >
    { dropdownItems && dropdownItems.map(o => <option key={ o.value } value={ o.value }>{ o.label }</option>) }
  </select>);
};

QuestionIgaSubClassificationDropdownField.displayName = 'QuestionIgaSubClassificationDropdownField';

QuestionIgaSubClassificationDropdownField.propTypes = {
  intl: PropTypes.shape().isRequired,
  blockId: PropTypes.string.isRequired,
  question: PropTypes.shape().isRequired,
  className: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  jurisdiction: PropTypes.shape()
};

QuestionIgaSubClassificationDropdownField.defaultProps = {
  className: '',
  jurisdiction: null
};

export default QuestionIgaSubClassificationDropdownField;
